import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { X, Eye, Download, Calendar, DollarSign, TrendingUp, Loader, Trash2 } from 'lucide-react';
import { deleteValuation } from '../services/api';
import logger from '../utils/logger';
import { formatDateTime } from '../utils/dateUtils';
import { useTimezone } from '../contexts/TimezoneContext';

const ValuationHistoryModal = ({ isOpen, onClose, valuations, onLoad, onDelete, valuationType }) => {
  const { timezone } = useTimezone();
  const [selectedValuation, setSelectedValuation] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  if (!isOpen) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getMethodName = (method) => {
    const methods = {
      'cca': 'CCA',
      'dcf': 'DCF',
      'manual': 'Manual',
      'capitalization': 'Cap. Earnings',
      'nav': 'NAV',
      'comprehensive': 'Comprehensive'
    };
    return methods[method] || method;
  };

  const handleViewDetails = (valuation) => {
    setSelectedValuation(valuation);
    setShowDetails(true);
  };

  const handleLoadValuation = (valuation) => {
    onLoad(valuation);
    onClose();
  };

  const handleDeleteValuation = async (valuationId) => {
    setDeletingId(valuationId);
    try {
      const result = await deleteValuation(valuationId);
      if (result.success) {
        // Call the parent's onDelete callback to refresh the list
        if (onDelete) {
          onDelete(valuationId);
        }
        setShowDeleteConfirm(null);
      } else {
        alert(result.error || 'Failed to delete valuation');
      }
    } catch (error) {
      logger.error('Error deleting valuation', { error, valuationId });
      alert('Failed to delete valuation');
    } finally {
      setDeletingId(null);
    }
  };

  const renderDetailedView = () => {
    if (!selectedValuation) return null;

    let inputs = {};
    try {
      inputs = typeof selectedValuation.input_data === 'string'
        ? JSON.parse(selectedValuation.input_data)
        : selectedValuation.input_data;
    } catch (e) {
      logger.error('Error parsing valuation input data', { error: e, valuationId: selectedValuation.id });
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-2 sm:p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowDetails(false)}
                className="text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-2 min-h-[44px]"
              >
                <span>← Back to List</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h3 className="text-2xl font-bold text-gray-900">Valuation Details</h3>
            </div>
            <button
              onClick={() => setShowDetails(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Summary Card */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Valuation</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatCurrency(selectedValuation.valuation_amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Method</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {getMethodName(selectedValuation.method)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Type</p>
                    <p className="text-lg font-semibold text-gray-900 capitalize">
                      {selectedValuation.type || 'Simple'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatDateTime(selectedValuation.created_at, timezone)}
                    </p>
                  </div>
                </div>

                {selectedValuation.low_range && selectedValuation.high_range && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <p className="text-sm text-gray-600">Valuation Range</p>
                    <p className="text-md font-semibold text-gray-900">
                      {formatCurrency(selectedValuation.low_range)} - {formatCurrency(selectedValuation.high_range)}
                    </p>
                  </div>
                )}
              </div>

              {/* Financial Inputs */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Financial Inputs</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {inputs.revenue && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-700">Revenue:</span>
                      <span className="font-semibold text-gray-900">
                        {typeof inputs.revenue === 'object'
                          ? formatCurrency(Object.values(inputs.revenue)[Object.values(inputs.revenue).length - 1])
                          : formatCurrency(inputs.revenue)
                        }
                      </span>
                    </div>
                  )}
                  {inputs.ebitda && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-700">EBITDA:</span>
                      <span className="font-semibold text-gray-900">
                        {typeof inputs.ebitda === 'object'
                          ? formatCurrency(Object.values(inputs.ebitda)[Object.values(inputs.ebitda).length - 1])
                          : formatCurrency(inputs.ebitda)
                        }
                      </span>
                    </div>
                  )}
                  {(inputs.net_income || inputs.profit_after_tax) && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-700">Net Income:</span>
                      <span className="font-semibold text-gray-900">
                        {(() => {
                          const value = inputs.net_income || inputs.profit_after_tax;
                          return typeof value === 'object'
                            ? formatCurrency(Object.values(value)[Object.values(value).length - 1])
                            : formatCurrency(value);
                        })()}
                      </span>
                    </div>
                  )}
                  {inputs.cash_flow && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-700">Cash Flow:</span>
                      <span className="font-semibold text-gray-900">
                        {typeof inputs.cash_flow === 'object'
                          ? formatCurrency(Object.values(inputs.cash_flow)[Object.values(inputs.cash_flow).length - 1])
                          : formatCurrency(inputs.cash_flow)
                        }
                      </span>
                    </div>
                  )}
                  {inputs.total_assets && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-700">Total Assets:</span>
                      <span className="font-semibold text-gray-900">
                        {typeof inputs.total_assets === 'object'
                          ? formatCurrency(Object.values(inputs.total_assets)[Object.values(inputs.total_assets).length - 1])
                          : formatCurrency(inputs.total_assets)
                        }
                      </span>
                    </div>
                  )}
                  {inputs.total_liabilities && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-700">Total Liabilities:</span>
                      <span className="font-semibold text-gray-900">
                        {typeof inputs.total_liabilities === 'object'
                          ? formatCurrency(Object.values(inputs.total_liabilities)[Object.values(inputs.total_liabilities).length - 1])
                          : formatCurrency(inputs.total_liabilities)
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes if any */}
              {selectedValuation.notes && (
                <div className="bg-amber-50 rounded-lg border border-amber-200 p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Notes</h4>
                  <p className="text-gray-700">{selectedValuation.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-between">
            <button
              onClick={() => setShowDetails(false)}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px]"
            >
              Close
            </button>
            <button
              onClick={() => {
                handleLoadValuation(selectedValuation);
                setShowDetails(false);
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 min-h-[44px]"
            >
              <Download size={18} />
              Load to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Main History Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Valuation History</h3>
              <p className="text-sm text-gray-600 mt-1">
                {valuationType === 'simple' ? 'Simple' : 'Advanced'} Valuation Records
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {valuations.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No historical valuations found.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {valuations.map((valuation) => (
                  <div
                    key={valuation.id}
                    className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col gap-4">
                      {/* Info Section */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Valuation Amount */}
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Valuation</p>
                          <p className="text-lg font-bold text-blue-600">
                            {formatCurrency(valuation.valuation_amount)}
                          </p>
                        </div>

                        {/* Method */}
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Method</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {getMethodName(valuation.method)}
                          </p>
                        </div>

                        {/* Date */}
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Created</p>
                          <p className="text-sm text-gray-700 flex items-center gap-1">
                            <Calendar size={14} />
                            {formatDateTime(valuation.created_at, timezone)}
                          </p>
                        </div>

                        {/* Range (if available) */}
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Range</p>
                          {valuation.low_range && valuation.high_range ? (
                            <p className="text-xs text-gray-700">
                              {formatCurrency(valuation.low_range)} - {formatCurrency(valuation.high_range)}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-400">N/A</p>
                          )}
                        </div>
                      </div>

                      {/* Actions Section */}
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                        <button
                          onClick={() => handleViewDetails(valuation)}
                          className="px-4 py-2 text-sm border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-1"
                        >
                          <Eye size={16} />
                          View
                        </button>
                        <button
                          onClick={() => handleLoadValuation(valuation)}
                          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                        >
                          <Download size={16} />
                          Load
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(valuation.id)}
                          disabled={deletingId === valuation.id}
                          className="px-4 py-2 text-sm border border-red-600 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingId === valuation.id ? (
                            <Loader size={16} className="animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="w-full px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px]"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Detailed View Modal */}
      {showDetails && renderDetailedView()}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Valuation
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete this valuation? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteValuation(showDeleteConfirm)}
                    disabled={deletingId === showDeleteConfirm}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[44px]"
                  >
                    {deletingId === showDeleteConfirm ? (
                      <>
                        <Loader size={16} className="animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

ValuationHistoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  valuations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      created_at: PropTypes.string.isRequired,
      valuation_method: PropTypes.string,
      valuation_result: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      input_data: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
    })
  ).isRequired,
  onLoad: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  valuationType: PropTypes.string
};

ValuationHistoryModal.defaultProps = {
  valuationType: 'valuation'
};

export default ValuationHistoryModal;
